import React, { useState, useCallback } from 'react';
import { ChatInput } from '../ChatInput';
import type { TagChip } from '../ChatInput/ChatInput.types';
import type { CABTag } from '../../types/cabTags';
import { useAuth } from '../../contexts/AuthContext';
import { useTagSearch } from '../../hooks/useTagSearch';
import { serializeTagsIntoContent } from '../../utils/cabTagSerializer';

interface CABChatInputProps {
  projectId: string;
  onSend: (content: string, tags: CABTag[], modelPreference?: string) => Promise<void>;
  isSending: boolean;
  disabled?: boolean;
  placeholder?: string;
}

interface TagSuggestion {
  label: string;
  jsonPath: string[];
}

interface TagSuggestResponse {
  suggestions: TagSuggestion[];
  query: string;
}

const SECTION_LABELS: Record<string, string> = {
  dataModel: 'Data Model',
  entities: 'Entities',
  screens: 'Screens',
  workflows: 'Workflows',
  rules: 'Rules',
  userStories: 'User Stories',
  reporting: 'Reporting',
  integrations: 'Integrations',
  suggestedQuestions: 'Suggested Questions',
  roles: 'Roles',
};

export const CABChatInput: React.FC<CABChatInputProps> = ({
  projectId,
  onSend,
  isSending,
  disabled = false,
  placeholder = 'Ask about your CAB or request changes...',
}) => {
  const [message, setMessage] = useState('');
  const [selectedTags, setSelectedTags] = useState<CABTag[]>([]);
  const { token } = useAuth();

  const mapSuggestions = useCallback((data: TagSuggestResponse): TagChip[] =>
    (data?.suggestions || []).map((tag) => ({
      id: `${tag.jsonPath.join('-')}-${tag.label}`,
      label: tag.label,
      sectionName: SECTION_LABELS[tag.jsonPath[0]] || tag.jsonPath[0],
      data: { jsonPath: tag.jsonPath },
    })), []);

  const { suggestions, isSearching, handleTagSearch } = useTagSearch({
    endpoint: `/cab/${projectId}/tags/suggest`,
    token,
    mapResponse: mapSuggestions,
  });

  const handleTagsChange = useCallback((tagChips: TagChip[]) => {
    const cabTags = tagChips.map(chip => ({
      id: chip.id,
      label: chip.label,
      jsonPath: chip.data?.jsonPath || [],
    }));
    setSelectedTags(cabTags);
  }, []);

  const handleSend = useCallback(async (content: string, attachments?: any[], modelPreference?: string) => {
    const contentWithTags = serializeTagsIntoContent(content, selectedTags);
    await onSend(contentWithTags, selectedTags, modelPreference);
    setMessage('');
    setSelectedTags([]);
  }, [onSend, selectedTags]);

  return (
    <ChatInput
      value={message}
      onChange={setMessage}
      onSend={handleSend}
      placeholder={placeholder}
      disabled={disabled}
      isSending={isSending}
      projectId={projectId}
      showAttachmentButton={false}
      showModelSelector={true}
      enableTagChips={true}
      onTagSearch={handleTagSearch}
      onTagsChange={handleTagsChange}
      tagSuggestions={suggestions}
      isTagSearching={isSearching}
    />
  );
};
